/**
 * Sheets-side transaction-log vocabulary — slice **TX0** of the SDK-2.0 engine
 * spec (§4). Binds the generic `TransactionLog<Op>` (`@tento-core/transaction-log`)
 * to `SheetTxOp` — the JSON-safe union of every §7.1 store write point — and
 * wraps it in a `SheetTxRecorder` that rides the `LedgerBumper` (the one object
 * every registered bump funnels through). Dark in TX0: nothing reads the log to
 * make a decision; it is an audit + fixture substrate only.
 *
 * ## TDZ rule — this is a LEAF module (see ledger-keys.ts header + learning
 * `2026-07-09-sheets-module-tdz-cycle-ledger-keys`)
 *
 * `rows/variants/source-row.ts` and the cell store both pull this module for their
 * bump records, and `rows/variants/computed-data-source.ts` extends `SourceRow`. So
 * this module **MUST NOT** value-import `../rows/row-address` (or anything that pulls
 * the variant classes) — that would close the module-eval cycle
 * `source-row → tx-log → row-address → computed-data-source → source-row` and
 * TDZ-crash on the `extends SourceRow` evaluation. It value-imports **only**
 * `@tento-core/transaction-log` + `@tento-core/tracing` (both external to
 * sheets, no cycle). Any Ledger / `DependencyAspect` / chrono reference here
 * MUST be `import type` (erased, no eval edge) — currently none are needed
 * (dates/keys/facets cross the boundary as plain strings).
 *
 * ## How the variant bulk sites open scopes without a value-import (the TX0
 * TDZ resolution)
 *
 * Variants **MUST NOT** value-import this module, so `TxScope.run` (below) is
 * NOT how they scope. Instead the null-tolerant scope helper lives on
 * `LedgerBumper` (`bumper.txScope(label, fn)`, ledger-keys.ts): variants already
 * reach the bumper through `sheet.stores.sourceCellData.ledgerBumper` (the L3
 * reachability pattern) and call `.txScope` on it — a method call on an
 * already-typed object, no import. `bumper.txScope` runs `fn` inside
 * `recorder.scope` when a recorder is attached, else runs it directly, so it is
 * safe whether or not the log exists. `TxScope.run` is the equivalent helper for
 * non-variant, non-TDZ callers (tests, future SDK glue) that CAN import this
 * leaf.
 */

import type { Option } from "../core/option.ts";
import { TransactionLog } from "@tento-core/transaction-log";
import { Tracing } from "@tento-core/tracing";
import { CellId } from "../ids/ids.ts";

/** Max length of a serialized cell-value summary — bounded + deterministic. */
const SUMMARY_MAX = 48;

/** Flush a `txlog/transactions` breadcrumb every this-many seals. */
const TXLOG_FLUSH_BATCH = 64;

/**
 * The **detail** half of a `SheetTxOp` — the op discriminant `t` plus its
 * payload, WITHOUT the `key` (the `data:` / `attr:` ledger node) and `v` (the
 * post-bump version). The store write points build a detail; `LedgerBumper`
 * stamps `key` + `v` at the bump. Every field is JSON-safe (dates / cell ids as
 * strings; values pre-summarized).
 */
export type SheetTxDetail =
  | { readonly t: "cell/set"; readonly date: string; readonly cellId: Option<CellId>; readonly value: SheetTxOp.ValueSummary }
  | { readonly t: "cell/clear"; readonly date: string; readonly cellId: Option<CellId> }
  | { readonly t: "cell/set-custom"; readonly cellId: CellId; readonly value: SheetTxOp.ValueSummary }
  | { readonly t: "cell/remove-custom"; readonly cellId: CellId }
  | { readonly t: "cell/replace-custom-range"; readonly count: number }
  | { readonly t: "cell/forget" }
  | { readonly t: "attr/patch"; readonly fields: readonly string[] }
  | { readonly t: "range/commit"; readonly start: string; readonly end: string };

/**
 * A recorded store write OR a TX2 gesture control op. Store writes carry `key`
 * (the ledger node they bumped) + `v` (the POST-bump version — one record per
 * bump): the detail kinds stamped with the envelope, plus the minimal `bump`
 * fallback that plain `LedgerBumper.bump` emits for un-migrated / preference /
 * tz write points. Control ops (`SheetTxOp.Control`) carry NO ledger key/version
 * — they are the durable-horizon journal (durable-ack / rollback) appended
 * referencing a frame-commit `F`, never a store mutation.
 */
export type SheetTxOp =
  | (SheetTxDetail & { readonly key: string; readonly v: number })
  | SheetTxOp.Bump
  | SheetTxOp.Control;

export namespace SheetTxOp {
  /** Bounded, deterministic value summary: a coarse dtype tag + truncated JSON. */
  export interface ValueSummary {
    readonly t: string;
    readonly s: string;
  }

  /**
   * The minimal fallback record for a bump with no enriched detail — the
   * preference / timezone / not-yet-migrated write points. Carries just the
   * ledger `facet` + counter `bucket` beside `key` + `v`.
   */
  export interface Bump {
    readonly t: "bump";
    readonly key: string;
    readonly facet: string;
    readonly bucket: string;
    readonly v: number;
  }

  /**
   * A TX2 gesture control op — NOT a store write, NOT a bump. Appended to the
   * WAL after a frame-commit `F` (an intent-stamped `scopeIntent` txn) to record
   * the durable-horizon evolution of `F`:
   *
   *  - `txn/durable-ack{ofTxId:F, step}` — a backend ack for `F`'s `step`-th
   *    optimistic op landed durably.
   *  - `txn/rollback{ofTxId:F, …}` — `F` was unwound (a backend nack after
   *    compensating the confirmed steps), carrying `F`'s intent, the bounded
   *    error tag, the confirmed-step count, and whether any durable work landed.
   *
   * The frozen sealed `F` envelope is NEVER mutated — `F`'s durable horizon is
   * DERIVED by folding these control ops. With the sequential runner acks arrive
   * in `step` order, so the durable mark advances over a contiguous acked prefix;
   * this is an invariant of the sequential runner, NOT something the log enforces.
   */
  export type Control =
    | { readonly t: "txn/durable-ack"; readonly ofTxId: number; readonly step: number }
    | {
        readonly t: "txn/rollback";
        readonly ofTxId: number;
        readonly intent: Option<string>;
        readonly errorTag: string;
        readonly steps: number;
        readonly durable: boolean;
      };

  /** Every op discriminant — the recorder's per-kind counter buckets. */
  export type Kind = SheetTxOp["t"];

  /**
   * Summarize a cell value into a bounded, JSON-safe `{t,s}`. `t` is a coarse
   * runtime tag (`typeof`, with `null` / `arr` specialized); `s` is the JSON
   * form truncated at `SUMMARY_MAX`. Deterministic for a given value — no
   * wall-clock, no identity. Non-serializable values degrade to `"?"`.
   */
  export function summarize(value: unknown): ValueSummary {
    return { t: valueTag(value), s: stringifyBounded(value) };
  }

  /** Stamp a `SheetTxDetail` with its ledger `key` + post-bump version `v`. */
  export function fromDetail(detail: SheetTxDetail, key: string, v: number): SheetTxOp {
    return { ...detail, key, v };
  }
}

function valueTag(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "arr";
  return typeof value;
}

/**
 * Characters `JSON.stringify` escapes inside a string: the quote, the
 * backslash, C0 controls, the two line separators it escapes for
 * JS-safety (U+2028/2029), and surrogate code units (well-formed stringify
 * escapes lone ones; pairs are rare enough to share the slow path).
 */
const NEEDS_ESCAPE = /["\\\u0000-\u001f\u2028\u2029\ud800-\udfff]/;

/**
 * The first `SUMMARY_MAX` characters of `JSON.stringify(value)`, computed
 * without serializing the rest of the value.
 *
 * Why: a summary is recorded for every `cell/set`, and a calendar cell's
 * value is an events array of objects holding `DateTime`s. `JSON.stringify`
 * walked the whole array — every nested `toJSON` — to produce 48 characters,
 * and a hydrate or timescale switch does that thousands of times in one
 * frame (~0.3-0.4s of stringify + `DateTime.toJSON` per switch battery in
 * the sdk2 traces). The walker below emits the same characters in the same
 * order and stops as soon as the budget is spent.
 *
 * Parity contract (a repository test pins it): for every value,
 * the result equals `JSON.stringify(value).slice(0, SUMMARY_MAX)`, with the
 * two fallbacks the old form had — `undefined` (stringify returns no string)
 * summarizes as "null", and a throw (cycles, BigInt) as "?". The walker
 * follows the stringify algorithm: `toJSON` first, own enumerable string
 * keys in `Object.keys` order, `undefined`/function/symbol values dropped
 * from objects and written as `null` in arrays, boxed primitives unboxed,
 * non-finite numbers as `null`.
 */
function stringifyBounded(value: unknown): string {
  const w = new BoundedJsonWriter(SUMMARY_MAX);
  try {
    if (!w.write(value, "")) return "null"; // top-level undefined
  } catch {
    return "?";
  }
  return w.out.length > SUMMARY_MAX ? w.out.slice(0, SUMMARY_MAX) : w.out;
}

class BoundedJsonWriter {
  out = "";
  /** Objects on the current path — a revisit is a cycle, which stringify
   * reports by throwing (TypeError), and the summary as "?". A stack, not a
   * `Set`: the path is a few objects deep, and a summary is written for every
   * cell a store sets (a `Set` per summary was its largest allocation). */
  readonly #path: object[] = [];
  readonly #budget: number;
  constructor(budget: number) {
    this.#budget = budget;
  }

  #full(): boolean {
    return this.out.length >= this.#budget;
  }

  /**
   * Append `value`'s serialization. Returns false when stringify would emit
   * nothing for it (undefined, function, symbol) so an object can drop the
   * property and an array can write `null` instead.
   */
  write(value: unknown, key: string): boolean {
    // 1. toJSON, exactly as stringify: called with the key, on any object
    //    (Date, DateTime, …) — and on BigInt, which has none and throws.
    if (value !== null && (typeof value === "object" || typeof value === "bigint")) {
      const toJSON = (value as { toJSON?: unknown }).toJSON;
      if (typeof toJSON === "function") value = toJSON.call(value, key);
    }
    // 2. Boxed primitives unbox.
    if (typeof value === "object" && value !== null) {
      if (value instanceof Number) value = Number(value);
      else if (value instanceof String) value = String(value);
      else if (value instanceof Boolean) value = Boolean(value.valueOf());
    }
    switch (typeof value) {
      case "undefined":
      case "function":
      case "symbol":
        return false;
      case "bigint":
        throw new TypeError("BigInt");
      case "boolean":
        this.out += value ? "true" : "false";
        return true;
      case "number":
        this.out += Number.isFinite(value) ? String(value) : "null";
        return true;
      case "string":
        this.out += NEEDS_ESCAPE.test(value) ? JSON.stringify(value) : '"' + value + '"';
        return true;
    }
    if (value === null) {
      this.out += "null";
      return true;
    }
    const obj = value as object;
    if (this.#path.includes(obj)) throw new TypeError("cycle");
    this.#path.push(obj);
    try {
      if (Array.isArray(obj)) {
        this.out += "[";
        for (let i = 0; i < obj.length; i++) {
          if (i > 0) this.out += ",";
          if (this.#full()) return true;
          if (!this.write(obj[i], String(i))) this.out += "null";
        }
        this.out += "]";
      } else {
        this.out += "{";
        let first = true;
        for (const k of Object.keys(obj)) {
          if (this.#full()) return true;
          const mark = this.out.length;
          this.out += (first ? "" : ",") + (NEEDS_ESCAPE.test(k) ? JSON.stringify(k) : '"' + k + '"') + ":";
          if (this.write((obj as Record<string, unknown>)[k], k)) first = false;
          else this.out = this.out.slice(0, mark); // dropped property: rewind
        }
        this.out += "}";
      }
    } finally {
      this.#path.pop();
    }
    return true;
  }
}

/**
 * The payload of a `txn/rollback` control op — mirrors `SheetTxOp.Control`'s
 * rollback arm one-to-one. Built by the gesture runner ({@link MutationTransaction})
 * when a frame-commit `F` is unwound, and carried on the thrown
 * `GestureRollbackError` so a caller can inspect the horizon without re-reading
 * the WAL.
 */
export interface RollbackRecord {
  /** The frame-commit txn `F` being unwound. */
  readonly ofTxId: number;
  /** `F`'s semantic intent (the gesture intent). */
  readonly intent: Option<string>;
  /** Bounded error tag (error name only) of the forward failure. */
  readonly errorTag: string;
  /** How many of `F`'s steps had confirmed durably before the failure. */
  readonly steps: number;
  /** Whether any durable work landed (`steps > 0`) — a pre-durable failure is
   *  `false` (nothing to compensate). */
  readonly durable: boolean;
}

/**
 * The narrow WRITE-side surface the TX2 gesture runner needs from the WAL — the
 * mirror of the read-side `TxJoinSource` (`sheets/src/render/frame/tx-frame-join.ts`).
 * Satisfied STRUCTURALLY by {@link SheetTxRecorder}. A null `GestureTxWriter`
 * makes a gesture run fully dark (no WAL journalling), so the runner's optimistic
 * behavior is byte-identical whether or not a recorder is attached.
 */
export interface GestureTxWriter {
  /** Open an intent-stamped frame-commit scope (the sealed txn carries `intent`). */
  scopeIntent<T>(label: string, intent: string, fn: () => T): T;
  /** Append a `txn/durable-ack{ofTxId, step}` control op (its own tiny scope). */
  recordDurableAck(ofTxId: number, step: number): void;
  /** Append a `txn/rollback` control op (its own tiny scope) + a breadcrumb. */
  recordRollback(r: RollbackRecord): void;
  /** The read mark τ — the last sealed txId; read right after a frame-commit
   *  seals to capture `F`. */
  tau(): number;
}

/**
 * The sheets-side recorder — wraps a `TransactionLog<SheetTxOp>` and owns the
 * `txlog/transactions` counter aggregate (per-kind record counts, txns-by-origin,
 * committed / aborted / dropped) beside the log, plus one batched
 * `Tracing.log("txlog/transactions", …)` every `TXLOG_FLUSH_BATCH` seals (never
 * per-op). The channel measures transaction volume by op kind and origin and
 * the commit / abort / drop outcomes.
 *
 * `record` is called once per `LedgerBumper` bump, so `recordCount` equals
 * `bumper.totalBumps()` over the window the recorder has been attached — the
 * one-op-record-per-bump invariant (engine-spec §4).
 */
export class SheetTxRecorder {
  readonly log: TransactionLog<SheetTxOp>;

  // Per-kind record counts.
  readonly #kindCounts: Record<SheetTxOp.Kind, number> = {
    "cell/set": 0,
    "cell/clear": 0,
    "cell/set-custom": 0,
    "cell/remove-custom": 0,
    "cell/replace-custom-range": 0,
    "cell/forget": 0,
    "attr/patch": 0,
    "range/commit": 0,
    "bump": 0,
    "txn/durable-ack": 0,
    "txn/rollback": 0,
  };
  // Txns sealed per origin ("frame" / scope labels).
  readonly #originCounts = new Map<string, number>();
  // Seals since the last Tracing flush.
  #sinceFlush = 0;

  constructor(opts: SheetTxRecorder.Options) {
    this.log = new TransactionLog<SheetTxOp>({
      scheduler: opts.scheduler,
      retain: opts.retain ?? null,
      retainOps: opts.retainOps ?? null,
      retainOpsPolicy: opts.retainOpsPolicy ?? null,
      onSeal: (txn) => this.#onSeal(txn),
    });
  }

  /** Record one op — the single per-bump write point. */
  record(op: SheetTxOp): void {
    this.#kindCounts[op.t] += 1;
    this.log.record(op);
  }

  /** Open a labeled explicit transaction (transparent when already scoped). */
  scope<T>(label: string, fn: () => T): T {
    return this.log.scope(label, fn);
  }

  /** Open an intent-stamped frame-commit scope (TX2). The sealed txn carries the
   *  intent and becomes the durable-horizon anchor `F` — see `GestureTxWriter`. */
  scopeIntent<T>(label: string, intent: string, fn: () => T): T {
    return this.log.scopeIntent(label, intent, fn);
  }

  /**
   * Append a `txn/durable-ack{ofTxId, step}` control op, recorded inside its own
   * tiny `"durable/ack"` scope so it seals as a standalone attributable txn —
   * never smeared into an unrelated open frame.
   */
  recordDurableAck(ofTxId: number, step: number): void {
    this.scope("durable/ack", () => {
      this.record({ t: "txn/durable-ack", ofTxId, step });
    });
  }

  /**
   * Append a `txn/rollback` control op inside its own tiny `"durable/rollback"`
   * scope (attributable, never smeared), then emit one bounded breadcrumb — the
   * never-silent contract for a horizon unwind. Only bounded fields are logged
   * (no unbounded payloads).
   */
  recordRollback(r: RollbackRecord): void {
    this.scope("durable/rollback", () => {
      this.record({
        t: "txn/rollback",
        ofTxId: r.ofTxId,
        intent: r.intent,
        errorTag: r.errorTag,
        steps: r.steps,
        durable: r.durable,
      });
    });
    Tracing.err("ui")("txlog/rollback", {
      ofTxId: r.ofTxId,
      intent: r.intent,
      errorTag: r.errorTag,
      steps: r.steps,
      durable: r.durable,
    });
  }

  /** Total ops recorded — equals `bumper.totalBumps()` since attach. */
  get recordCount(): number {
    return this.log.counters().ops;
  }

  /** Sealed transactions (retained window), in seal order. */
  transactions(): readonly TransactionLog.Transaction<SheetTxOp>[] {
    return this.log.transactions();
  }

  /** The read mark τ — the last sealed txId. */
  tau(): number {
    return this.log.tau();
  }

  /** Pull-based aggregate snapshot for tests. */
  snapshot(): SheetTxRecorder.Snapshot {
    const c = this.log.counters();
    return {
      records: c.ops,
      byKind: { ...this.#kindCounts },
      byOrigin: Object.fromEntries(this.#originCounts),
      committed: c.committed,
      aborted: c.aborted,
      dropped: c.dropped,
      tau: c.tau,
    };
  }

  // Tally a seal; batch-flush a breadcrumb.
  #onSeal(txn: TransactionLog.Transaction<SheetTxOp>): void {
    this.#originCounts.set(txn.origin, (this.#originCounts.get(txn.origin) ?? 0) + 1);
    this.#sinceFlush += 1;
    if (this.#sinceFlush >= TXLOG_FLUSH_BATCH) this.#flushTracing();
  }

  // `txlog/transactions`: the snapshot aggregate, one Tracing.log per N seals
  // (never per op).
  #flushTracing(): void {
    if (this.#sinceFlush === 0) return;
    this.#sinceFlush = 0;
    const s = this.snapshot();
    Tracing.log("txlog/transactions", {
      records: s.records,
      committed: s.committed,
      aborted: s.aborted,
      dropped: s.dropped,
      byKind: s.byKind,
      byOrigin: s.byOrigin,
    });
  }
}

export namespace SheetTxRecorder {
  export interface Options {
    readonly scheduler: TransactionLog.Scheduler;
    /** Cap on the committed list; oldest sealed txns dropped + counted. */
    readonly retain?: number;
    /** Cap on retained ops, subject to `retainOpsPolicy`. */
    readonly retainOps?: number;
    /** Default `keep-newest` permits an oversized newest txn; `strict` evicts it whole. */
    readonly retainOpsPolicy?: "keep-newest" | "strict";
  }

  /** The pull aggregate the batched breadcrumb + tests read. */
  export interface Snapshot {
    readonly records: number;
    readonly byKind: Readonly<Record<SheetTxOp.Kind, number>>;
    readonly byOrigin: Readonly<Record<string, number>>;
    readonly committed: number;
    readonly aborted: number;
    readonly dropped: number;
    readonly tau: number;
  }
}

/**
 * Null-tolerant labeled scope for callers that CAN value-import this leaf
 * (tests, non-TDZ SDK glue). Runs `fn` inside `recorder.scope` when a recorder
 * is present, else runs it directly. Variants MUST NOT use this (they cannot
 * value-import tx-log) — they call `bumper.txScope` instead.
 */
export namespace TxScope {
  export function run<T>(
    recorder: Option<SheetTxRecorder>,
    label: string,
    fn: () => T,
  ): T {
    return recorder != null ? recorder.scope(label, fn) : fn();
  }
}
